/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
*/
import isString from 'lodash.isstring';
import pickBy from 'lodash.pickby';
import {getAccountId} from '@ion/applications/src/helpers';
import {
  errorAddOptions,
  errorEditOptions,
  successAddOptions,
  successEditOptions
} from '@ion/applications/src/prompts/constants';
import {error} from '@ion/core/src/logger/logger';
import {
  createPegLabel,
  deletePegLabel,
  deletePegLabels,
  getPegLabel,
  getPegLabels,
  updatePegLabel
} from '@ion/core/src/services/Connect';
import {
  AuditChange,
  Entity,
  PegLabel,
  PegLabels,
  PegLabelsFilters
} from '@ion/core/src/types/entities';
import {LoadPegLabelsParams} from '@ion/core/src/types/services';
import {retryUpdate} from '@ion/services/src/helpers/retry';
import {PageInitializeAction} from '@/types/redux';
import {toggleUserPrompt} from './user-prompt';

// Defines the name of the page label's reducer
export const REDUCER_NAME = 'pegLabels';

export const INITIALIZE_ADMIN_PEG_LABELS               = 'INITIALIZE_ADMIN_PEG_LABELS';
export const SET_ADMIN_PEG_LABELS_GRID_COLUMNS         = 'SET_ADMIN_PEG_LABELS_GRID_COLUMNS';
export const UPDATE_ADMIN_PEG_LABELS_GRID_COLUMNS      = 'UPDATE_ADMIN_PEG_LABELS_GRID_COLUMNS';
export const ADMIN_PEG_LABELS_GRID_REFRESHED           = 'ADMIN_PEG_LABELS_GRID_REFRESHED';
export const SET_GRID_PAGE_SIZE                        = 'SET_PEG_LABELS_GRID_PAGE_SIZE';
export const GET_ADMIN_PEG_LABELS_REQUEST              = 'GET_ADMIN_PEG_LABELS_REQUEST';
export const GET_ADMIN_PEG_LABELS_RESPONSE             = 'GET_ADMIN_PEG_LABELS_RESPONSE';
export const GET_ADMIN_PEG_LABELS_ERROR                = 'GET_ADMIN_PEG_LABELS_ERROR';
export const DISPLAY_DELETE_PEG_LABELS_DIALOG          = 'DISPLAY_DELETE_PEG_LABELS_DIALOG';
export const DELETE_ADMIN_PEG_LABELS_REQUEST           = 'DELETE_ADMIN_PEG_LABELS_REQUEST';
export const DELETE_ADMIN_PEG_LABELS_RESPONSE          = 'DELETE_ADMIN_PEG_LABELS_RESPONSE';
export const DELETE_ADMIN_PEG_LABELS_ERROR             = 'DELETE_ADMIN_PEG_LABELS_ERROR';
export const ADD_ADMIN_PEG_LABEL_REQUEST               = 'ADD_ADMIN_PEG_LABEL_REQUEST';
export const ADD_ADMIN_PEG_LABEL_RESPONSE              = 'ADD_ADMIN_PEG_LABEL_RESPONSE';
export const ADD_ADMIN_PEG_LABEL_ERROR                 = 'ADD_ADMIN_PEG_LABEL_ERROR';
export const UPDATE_PEG_LABELS_SEARCH_QUERIES          = 'UPDATE_PEG_LABELS_SEARCH_QUERIES';
export const RESET_PEG_LABELS_SEARCH_QUERIES           = 'RESET_PEG_LABELS_SEARCH_QUERIES';
export const SHOW_ADD_PEG_LABEL_DIALOG                 = 'SHOW_ADD_PEG_LABEL_DIALOG';
export const SHOW_EDIT_PEG_LABEL_DIALOG                = 'SHOW_EDIT_PEG_LABEL_DIALOG';
export const EDIT_ADMIN_PEG_LABEL_REQUEST              = 'EDIT_ADMIN_PEG_LABEL_REQUEST';
export const EDIT_ADMIN_PEG_LABEL_RESPONSE             = 'EDIT_ADMIN_PEG_LABEL_RESPONSE';
export const EDIT_ADMIN_PEG_LABEL_ERROR                = 'EDIT_ADMIN_PEG_LABEL_ERROR';
export const AUDIT_ADMIN_PEG_LABEL                     = 'AUDIT_ADMIN_PEG_LABEL';
export const AUDIT_ADMIN_PEG_LABEL_SHOW_CHANGES_DIALOG = 'AUDIT_ADMIN_PEG_LABEL_SHOW_CHANGES_DIALOG';

// Dispatches an action to perform redux initialization for Peg Labels
export const initializeAdminPegLabels = (): PageInitializeAction => ({
  type : INITIALIZE_ADMIN_PEG_LABELS
});

// Dispatches an action when the Peg Labels grid has been refreshed
export const adminPegLabelsGridRefreshed = (): PageInitializeAction => {
  return {type: ADMIN_PEG_LABELS_GRID_REFRESHED};
};

// Updates the modular search queries for Peg Labels
export const updatePegLabelsSearchQueries = (searchParams: PegLabelsFilters) => ({
  type : UPDATE_PEG_LABELS_SEARCH_QUERIES, searchParams
});

// Resets the Peg Labels filters state
export const resetPegLabelsSearchQueries = () => ({
  type : RESET_PEG_LABELS_SEARCH_QUERIES
});

/**
 * Invoked when the the Peg Labels auditor is to be displayed or removed.
 */
export const auditPegLabels = (pegLabels: PegLabel, show = true) => ({
  type : AUDIT_ADMIN_PEG_LABEL, pegLabels, show
});

export const showAuditPegLabelsChangesDialog = (auditRecord: AuditChange, show = true) => ({
  type : AUDIT_ADMIN_PEG_LABEL_SHOW_CHANGES_DIALOG, auditRecord, show
});

// Dispatches actions related to retrieving Peg Labels from services
export const getAdminPegLabels = ({
  sort,
  callback,
  pageId = 1,
  pageSize = 20
}) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    dispatch({type: GET_ADMIN_PEG_LABELS_REQUEST});
    const {
      pegLabels: {
        search
      }
    } = getState();
    const endpointOptions: LoadPegLabelsParams = {
      accountId : getAccountId(getState),
      pageId,
      pageSize
    };
    if (sort) {
      endpointOptions.sortOn = sort;
    }
    const availableStringSearchAttributes = pickBy(search, isString);
    endpointOptions.filterOn = availableStringSearchAttributes;
    try {
      const {pegLabels: items, totalCount} = await getPegLabels(endpointOptions);
      dispatch({type: GET_ADMIN_PEG_LABELS_RESPONSE});
      callback?.({items, totalCount});
      return {items, totalCount};
    } catch (err) {
      error(err);
      callback({error: err, items: [], totalCount: 0});
      dispatch({type: GET_ADMIN_PEG_LABELS_ERROR, error: err});
      return err;
    }
  };
};

// Dispatches an action to show or hide the Peg Label deletion confirmation dialog to the user
export const displayDeleteDialog = (pegLabels: PegLabel | PegLabels, show = true) => ({
  type : DISPLAY_DELETE_PEG_LABELS_DIALOG, pegLabels, show
});

// Dispatches actions related to deleting a single or multiple (bulk) Peg Label(s)
export const deleteAdminPegLabels = (pegLabels: PegLabel | PegLabels) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: DELETE_ADMIN_PEG_LABELS_REQUEST});
    try {
      const service = Array.isArray(pegLabels) ? deletePegLabels : deletePegLabel;
      const deleted = await service(pegLabels as PegLabel & PegLabels);
      if (deleted.errors && deleted.errors.length > 0) {
        dispatch({type: DELETE_ADMIN_PEG_LABELS_ERROR, error: deleted.errors});
        return;
      }
      dispatch({type: DELETE_ADMIN_PEG_LABELS_RESPONSE, pegLabels: deleted});
    } catch (err) {
      dispatch({type: DELETE_ADMIN_PEG_LABELS_ERROR, error: err});
      error(err);
    }
  };
};

// Opens or closes the Add Peg Label dialog
export const showAddPegLabelDialog = (showDialog: boolean = true) => ({
  type : SHOW_ADD_PEG_LABEL_DIALOG, showDialog
});

// Dispatches actions related to creating the given Peg Label
export const addPegLabel = (pegLabelToAdd: PegLabel) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: ADD_ADMIN_PEG_LABEL_REQUEST});
    try {
      const pegLabels = await createPegLabel(pegLabelToAdd);
      dispatch({type: ADD_ADMIN_PEG_LABEL_RESPONSE, pegLabels});
      dispatch(toggleUserPrompt(successAddOptions(Entity.peglabel)));
    } catch (err) {
      dispatch({type: ADD_ADMIN_PEG_LABEL_ERROR, error: err});
      dispatch(toggleUserPrompt(errorAddOptions(Entity.peglabel, err)));
      error(err);
    }
  };
};

// Shows or hides the PEG Label edit dialog
export const showEditPegLabelDialog = (pegLabels: PegLabel, showDialog: boolean = true, isView: boolean = false) => ({
  type : SHOW_EDIT_PEG_LABEL_DIALOG, pegLabels, showDialog, isView
});

// Handles the dispatching of actions related to editing a PEG Label
export const editPegLabel = (pegLabel: PegLabel) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: EDIT_ADMIN_PEG_LABEL_REQUEST});
    try {
      const pegLabels = await retryUpdate({
        newEntity    : pegLabel,
        updateEntity : updatePegLabel,
        getEntity    : getSinglePegLabel,
        onError      : pegLabelUpdateError,
        dispatch
      });
      if (pegLabels) {
        dispatch({type: EDIT_ADMIN_PEG_LABEL_RESPONSE, pegLabels});
        dispatch(toggleUserPrompt(successEditOptions(Entity.peglabel)));
      }
    } catch (err) {
      pegLabelUpdateError(err, dispatch);
    }
  };
};

/**
 * Invoked when an existing Peg Label gets resource out of state, delegating to the fetch
 * latest peg label details successful response or error action accordingly.
 */
const getSinglePegLabel = async(PegLabelID: number) => {
  try {
    const pegLabel = await getPegLabel(PegLabelID);
    return {updated: pegLabel, error: ''};
  } catch (err) {
    error(err);
    return {updated: null, error: err};
  }
};

/**
 * Invoked when an existing Peg Label gets updated or fetched
 * and dispatching a successful or error action accordingly.
 */
const pegLabelUpdateError = (err, dispatch: Function) => {
  dispatch({type: EDIT_ADMIN_PEG_LABEL_ERROR, error: err});
  dispatch(toggleUserPrompt(errorEditOptions(Entity.peglabel, err)));
  error(err);
};

/**
 * Invoked whenever user change the row per page dropdown
 */
export const setPegLabelGridPageSize = (pageSize: number) => ({
  type : SET_GRID_PAGE_SIZE, pageSize
});
